import React, { ReactNode, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Chart } from "primereact/chart";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { Panel } from "primereact/panel";
import { ProgressSpinner } from "primereact/progressspinner";
import { useGetNotificationReportQuery } from "../../store/services/notification-logs";
import {
  NotificationLogType,
  NotificationReportGranularity,
  NotificationReportRuleBreakdown,
} from "../../models/notification-logs";
import { PageHeader } from "../../components/ui/PageHeader";
import { StatusBadge } from "../../components/ui/StatusBadge";
import { emptyMessage } from "../../components/ui/EmptyState";
import { DateRangeFilter } from "../../components/Filters/DateRangeFilter";
import {
  PERIOD_PRESETS,
  PeriodPreset,
  endOfDay,
  rangeForPreset,
  startOfDay,
} from "../Notifications/notificationLogPresentation";
import {
  buildVolumeChartData,
  formatFailureRate,
  ruleLabel,
  volumeChartOptions,
} from "../../utils/functions/notificationReportPresentation";

const typeOptions: { label: string; value: NotificationLogType | null }[] = [
  { label: "Todas", value: null },
  { label: "Manual", value: "MANUAL" },
  { label: "Automática", value: "AUTOMATIC" },
];

const granularityOptions: { label: string; value: NotificationReportGranularity }[] = [
  { label: "Por día", value: "day" },
  { label: "Por semana", value: "week" },
  { label: "Por mes", value: "month" },
];

const SummaryTile = ({
  icon,
  label,
  children,
}: {
  icon: string;
  label: string;
  children: ReactNode;
}) => (
  <div className="col-12 md:col-3">
    <div className="surface-ground border-1 surface-border border-round p-3 h-full">
      <div className="flex align-items-center gap-2 text-600 text-sm mb-2">
        <i className={`pi ${icon} text-primary`}></i>
        <span>{label}</span>
      </div>
      <div className="text-2xl font-semibold">{children}</div>
    </div>
  </div>
);

export const PushNotificationsReport = () => {
  const navigate = useNavigate();
  const today = useMemo(() => new Date(), []);

  const [preset, setPreset] = useState<PeriodPreset>("last30");
  const [customFrom, setCustomFrom] = useState<Date | null>(null);
  const [customTo, setCustomTo] = useState<Date | null>(null);
  const [type, setType] = useState<NotificationLogType | null>(null);
  const [granularity, setGranularity] = useState<NotificationReportGranularity>("day");

  const range = useMemo(() => {
    const presetRange = rangeForPreset(preset, today);
    if (presetRange) {
      return presetRange;
    }
    return {
      dateFrom: customFrom ? startOfDay(customFrom) : null,
      dateTo: customTo ? endOfDay(customTo) : null,
    };
  }, [preset, customFrom, customTo, today]);

  const rangeIsComplete = range.dateFrom !== null && range.dateTo !== null;

  const { data, isLoading, isFetching } = useGetNotificationReportQuery(
    {
      dateFrom: range.dateFrom?.toISOString() ?? "",
      dateTo: range.dateTo?.toISOString() ?? "",
      granularity,
      ...(type ? { type } : {}),
    },
    { skip: !rangeIsComplete }
  );

  const chartData = useMemo(
    () => (data ? buildVolumeChartData(data.volume, granularity) : null),
    [data, granularity]
  );

  const applyPreset = (next: PeriodPreset) => {
    setPreset(next);
    if (next !== "custom") {
      setCustomFrom(null);
      setCustomTo(null);
    }
  };

  return (
    <div className="p-card gap-4 p-3">
      <PageHeader title="Reporte de notificaciones" />

      <div className="flex flex-wrap align-items-end gap-3 mb-4 px-3">
        <div className="flex flex-column gap-2">
          <span className="text-600 text-sm">Período</span>
          <div className="flex flex-wrap gap-2">
            {PERIOD_PRESETS.map((option) => (
              <Button
                key={option.key}
                type="button"
                label={option.label}
                size="small"
                outlined={preset !== option.key}
                onClick={() => applyPreset(option.key)}
              />
            ))}
          </div>
        </div>

        {preset === "custom" && (
          <DateRangeFilter
            variant="stacked"
            columnClassName="flex flex-column gap-2"
            labelClassName="text-600 text-sm"
            labelFrom="Desde"
            labelTo="Hasta"
            calendarStyle={{ width: "11rem" }}
            dateFrom={customFrom}
            dateTo={customTo}
            onDateFromChange={setCustomFrom}
            onDateToChange={setCustomTo}
            startDateId="notificationReportDateFrom"
            endDateId="notificationReportDateTo"
            maxDateFrom={customTo ?? today}
            maxDateTo={today}
            minDateTo={customFrom ?? undefined}
          />
        )}

        <div className="flex flex-column gap-2">
          <label htmlFor="reportType" className="text-600 text-sm">
            Tipo
          </label>
          <Dropdown
            inputId="reportType"
            value={type}
            options={typeOptions}
            onChange={(event) => setType(event.value)}
            placeholder="Todas"
          />
        </div>

        <div className="flex flex-column gap-2">
          <label htmlFor="granularity" className="text-600 text-sm">
            Agrupar
          </label>
          <Dropdown
            inputId="granularity"
            value={granularity}
            options={granularityOptions}
            onChange={(event) => setGranularity(event.value)}
          />
        </div>
      </div>

      {!rangeIsComplete && (
        <div className="px-3 pb-4 text-600">
          Elegí una fecha desde y una fecha hasta para ver el reporte.
        </div>
      )}

      {rangeIsComplete && (isLoading || isFetching) && (
        <div className="flex justify-content-center p-5">
          <ProgressSpinner />
        </div>
      )}

      {rangeIsComplete && !isLoading && !isFetching && data && (
        <>
          <div className="grid px-3 mb-4">
            <SummaryTile icon="pi-send" label="Notificaciones enviadas">
              {data.totals.notifications}
            </SummaryTile>
            <SummaryTile icon="pi-user" label="Manual / Automática">
              {`${data.totals.manual} / ${data.totals.automatic}`}
            </SummaryTile>
            <SummaryTile icon="pi-users" label="Destinatarios">
              {data.totals.recipients}
            </SummaryTile>
            <SummaryTile icon="pi-exclamation-triangle" label="Tasa de fallos">
              {formatFailureRate(data.totals.failureRate)}
            </SummaryTile>
          </div>

          <Panel header="Volumen por período" className="mx-3 mb-4">
            {data.volume.length === 0 ? (
              <div className="text-600 py-4">{emptyMessage("notificaciones", true)}</div>
            ) : (
              <div style={{ height: "320px" }}>
                <Chart
                  type="bar"
                  data={chartData ?? { labels: [], datasets: [] }}
                  options={volumeChartOptions}
                  className="h-full w-full"
                />
              </div>
            )}
          </Panel>

          <Panel header="Desglose por regla" className="mx-3">
            <DataTable
              value={data.byRule}
              dataKey="triggerRule"
              responsiveLayout="scroll"
              emptyMessage={emptyMessage("notificaciones", true)}
              selectionMode="single"
              onRowClick={(event) => {
                const row = event.data as NotificationReportRuleBreakdown;
                const params = new URLSearchParams({ type: row.type });
                navigate(`/notifications/history?${params.toString()}`);
              }}
            >
              <Column
                header="Regla"
                body={(row: NotificationReportRuleBreakdown) => ruleLabel(row)}
                style={{ minWidth: "16rem" }}
              />
              <Column
                header="Tipo"
                body={(row: NotificationReportRuleBreakdown) => (
                  <StatusBadge
                    label={row.type === "MANUAL" ? "Manual" : "Automática"}
                    tone={row.type === "MANUAL" ? "info" : "pending"}
                  />
                )}
                style={{ width: "9rem" }}
              />
              <Column field="notifications" header="Envíos" style={{ width: "8rem" }} />
              <Column field="recipients" header="Destinatarios" style={{ width: "9rem" }} />
              <Column field="sent" header="Entregadas" style={{ width: "9rem" }} />
              <Column field="noToken" header="Sin dispositivo" style={{ width: "10rem" }} />
              <Column field="failed" header="Fallaron" style={{ width: "8rem" }} />
              <Column
                header="Tasa de fallos"
                body={(row: NotificationReportRuleBreakdown) =>
                  formatFailureRate(row.failureRate)
                }
                style={{ width: "10rem" }}
              />
            </DataTable>
          </Panel>
        </>
      )}
    </div>
  );
};
